﻿import { mutations } from "@/graphql";
import { useMutation } from "@apollo/client/react";
import { useCallback } from "react";
import { enqueueSnackbar } from "notistack";
import type { MutationDeleteSubscriptionFeatureArgs } from "@/types/graphql";

/**
 * Custom hook to delete a module.
 * @param {Function} [onCompleted] - Callback function to be called when the mutation completes successfully.
 * @param {Function} [onError] - Callback function to be called when an error occurs during the mutation.
 * @returns {{deleteModule: Function, deleting: boolean}} - The function to delete a module and the loading state.
 */
export function useDeleteFeature(
  onCompleted: (data?: any) => void = () => {},
  onError: (error?: any) => void = () => {},
) {
  const [deleteSubscriptionFeature, { loading }] = useMutation<
    any,
    MutationDeleteSubscriptionFeatureArgs
  >(mutations.deleteSubscriptionFeature, {
    onCompleted,
    onError: (err) => {
      onError(err);

      enqueueSnackbar("Delete Feature: " + err.message, {
        variant: "error",
      });
    },
    errorPolicy: "all",
  });

  const deleteSubscriptionFeatureRow = useCallback(
    (variables: MutationDeleteSubscriptionFeatureArgs) => {
      deleteSubscriptionFeature({
        variables,
        refetchQueries: ["GetSubscriptionFeatures"],
      }).then();
    },
    [deleteSubscriptionFeature],
  );

  return {
    deleteSubscriptionFeature: deleteSubscriptionFeatureRow,
    deleting: loading,
  };
}
